import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { UserRoleGroupsByKeywordQueryVariables, useUserRoleGroupsByKeywordQuery } from "../../graphql"
import { UserRoleGroupsList } from "./UserRoleGroupsList"

export type UserRoleGroupsByKeywordProps = {
	keyword: string
}

export const UserRoleGroupsByKeyword: React.FC<UserRoleGroupsByKeywordProps> = ({ keyword }) => {
	const [filter, setFilter] = useState({ keyword })

	const [pagination, setPagination] = useState<UserRoleGroupsByKeywordQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = useUserRoleGroupsByKeywordQuery({
		variables: { ...filter, pagination },
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ keyword })
		}, 1000)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword])

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.userRoleGroupsByKeyword.hasNextPage) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data, fetching])

	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.userRoleGroupsByKeyword ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading groups</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.userRoleGroupsByKeyword.userRoleGroups && data.userRoleGroupsByKeyword.userRoleGroups.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.userRoleGroupsByKeyword.userRoleGroups.length || 0} group
						{(data.userRoleGroupsByKeyword.userRoleGroups.length || 0) > 1 ? "s" : ""}
					</Text>

					<UserRoleGroupsList userRoleGroups={data.userRoleGroupsByKeyword.userRoleGroups} loadMore={nextPage} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more groups</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any groups.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
